<template>
<div class="st-page">
  <Navigation />
  <div class="container" style="padding-top:20px;max-width:1300px;">
    <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
      <div class="d-flex align-items-center gap-3">
        <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="st-back"><i class="mdi mdi-arrow-left"></i></a>
        <h4 class="st-page-title"><i class="mdi mdi-bus me-2"></i>Fleet Management</h4>
      </div>
      <button class="btn st-btn-primary btn-sm" @click="openVehicleForm()">+ Add Vehicle</button>
    </div>

    <!-- Alerts -->
    <div v-if="alerts.length" class="st-alert st-alert-danger" style="margin-bottom:12px;">
      <strong>⚠️ Expiring:</strong> <span v-for="(a,i) in alerts" :key="i">{{ a }}<span v-if="i<alerts.length-1"> · </span></span>
    </div>

    <!-- Summary -->
    <div class="fl-grid" v-if="dashboard">
      <div class="fl-card"><div class="fl-label">Vehicles</div><div class="fl-value">{{ dashboard.total_vehicles }}</div></div>
      <div class="fl-card"><div class="fl-label">Students Assigned</div><div class="fl-value" style="color:#2E86DE;">{{ dashboard.total_students }}</div><div class="fl-sub">of {{ dashboard.total_capacity }} capacity</div></div>
      <div class="fl-card"><div class="fl-label">Fuel This Month</div><div class="fl-value" style="color:#F59E0B;">KSh {{ fmt(dashboard.fuel_cost_month) }}</div></div>
      <div class="fl-card"><div class="fl-label">Maintenance</div><div class="fl-value" style="color:#EF4444;">KSh {{ fmt(dashboard.maintenance_cost_month) }}</div></div>
    </div>

    <!-- Tabs -->
    <div class="la-tabs">
      <button class="la-tab" :class="{active:tab==='vehicles'}" @click="tab='vehicles'">🚌 Vehicles</button>
      <button class="la-tab" :class="{active:tab==='fuel'}" @click="tab='fuel'; loadFuel()">⛽ Fuel</button>
      <button class="la-tab" :class="{active:tab==='maintenance'}" @click="tab='maintenance'; loadMaintenance()">🔧 Maintenance</button>
      <button class="la-tab" :class="{active:tab==='map'}" @click="tab='map'">📍 Live Map</button>
    </div>

    <!-- VEHICLES -->
    <div v-if="tab==='vehicles'">
      <div v-if="loading" class="st-spinner"><span class="spinner-border"></span></div>
      <div v-for="bus in buses" :key="bus.id" class="fl-bus-card" :class="'fl-status-'+bus.status">
        <div class="d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div style="flex:1;">
            <div class="d-flex align-items-center gap-2">
              <span class="fl-type-badge">{{ typeIcon(bus.vehicle_type) }}</span>
              <div>
                <div class="fl-bus-name">{{ bus.name }}</div>
                <div class="fl-bus-reg">{{ bus.registration_number }}</div>
              </div>
            </div>
            <div class="fl-bus-details mt-2">
              <span v-if="bus.route">📍 {{ bus.route }}</span>
              <span v-if="bus.driver_name">👤 {{ bus.driver_name }} <span v-if="bus.driver_phone">({{ bus.driver_phone }})</span></span>
              <span v-if="bus.make">🚗 {{ bus.make }} {{ bus.model }} {{ bus.year_of_manufacture || '' }}</span>
              <span v-if="bus.capacity">👥 {{ bus.assigned_students || 0 }}/{{ bus.capacity }} students</span>
              <span v-if="bus.odometer_km">🛣 {{ Number(bus.odometer_km).toLocaleString() }} km</span>
            </div>
            <div class="fl-bus-alerts mt-1">
              <span v-if="bus._insurance_warning" class="fl-alert-badge">⚠️ Insurance: {{ bus.insurance_expiry }}</span>
              <span v-if="bus._inspection_warning" class="fl-alert-badge">⚠️ Inspection: {{ bus.inspection_expiry }}</span>
              <span v-if="bus.gps_lat && bus.gps_updated_at" class="fl-gps-badge">📡 GPS {{ timeSince(bus.gps_updated_at) }}</span>
            </div>
          </div>
          <div class="d-flex flex-column gap-1" style="min-width:100px;">
            <span class="fl-status-badge" :class="'st-'+bus.status">{{ bus.status }}</span>
            <button class="btn st-btn-outline btn-sm" style="font-size:10px;padding:3px 8px;" @click="openVehicleForm(bus)">✏️ Edit</button>
            <button class="btn st-btn-outline btn-sm" style="font-size:10px;padding:3px 8px;" @click="openStudents(bus)">👥 Students</button>
            <button class="btn st-btn-outline btn-sm" style="font-size:10px;padding:3px 8px;" @click="openFuelForm(bus)">⛽ Fuel</button>
            <button class="btn st-btn-outline btn-sm" style="font-size:10px;padding:3px 8px;" @click="openMaintenanceForm(bus)">🔧 Service</button>
          </div>
        </div>
        <!-- Capacity bar -->
        <div v-if="bus.capacity > 0" style="margin-top:8px;">
          <div style="background:#1A3A6A;border-radius:4px;height:4px;overflow:hidden;">
            <div :style="{width:Math.min(100,(bus.assigned_students||0)*100/bus.capacity)+'%', background:(bus.assigned_students||0)>=bus.capacity?'#EF4444':'#10B981', height:'100%'}"></div>
          </div>
        </div>
      </div>
      <div v-if="!loading && !buses.length" style="text-align:center;padding:40px;color:#4A6A8A;"><div style="font-size:48px;">🚌</div><p>No vehicles. Add one to get started.</p></div>
    </div>

    <!-- FUEL TAB -->
    <div v-if="tab==='fuel'">
      <div class="st-section" style="padding:14px;">
        <div class="d-flex gap-2 align-items-end mb-3 flex-wrap">
          <div><label class="st-label" style="font-size:10px;">Vehicle</label><select class="st-input" v-model="fuelBusId" @change="loadFuel" style="width:180px;padding:8px;"><option value="">All</option><option v-for="b in buses" :key="b.id" :value="b.id">{{ b.name }}</option></select></div>
          <button class="btn st-btn-primary btn-sm" @click="openFuelForm()">+ Log Fuel</button>
        </div>
        <table v-if="fuelLogs.length" class="la-table">
          <thead><tr><th>Date</th><th>Vehicle</th><th>Litres</th><th>Cost</th><th>Odometer</th><th>Station</th></tr></thead>
          <tbody><tr v-for="f in fuelLogs" :key="f.id"><td>{{ f.date }}</td><td>{{ f.bus_name }}</td><td>{{ f.litres }}L</td><td style="color:#F59E0B;font-weight:600;">KSh {{ fmt(f.cost) }}</td><td>{{ f.odometer_km ? Number(f.odometer_km).toLocaleString()+' km' : '—' }}</td><td>{{ f.station || '—' }}</td></tr></tbody>
        </table>
        <div v-else style="text-align:center;padding:20px;color:#4A6A8A;">No fuel logs</div>
      </div>
    </div>

    <!-- MAINTENANCE TAB -->
    <div v-if="tab==='maintenance'">
      <div class="st-section" style="padding:14px;">
        <div class="d-flex gap-2 align-items-end mb-3 flex-wrap">
          <div><label class="st-label" style="font-size:10px;">Vehicle</label><select class="st-input" v-model="maintBusId" @change="loadMaintenance" style="width:180px;padding:8px;"><option value="">All</option><option v-for="b in buses" :key="b.id" :value="b.id">{{ b.name }}</option></select></div>
          <button class="btn st-btn-primary btn-sm" @click="openMaintenanceForm()">+ Log Service</button>
        </div>
        <table v-if="maintLogs.length" class="la-table">
          <thead><tr><th>Date</th><th>Vehicle</th><th>Type</th><th>Description</th><th>Cost</th><th>Vendor</th><th>Next Due</th></tr></thead>
          <tbody><tr v-for="m in maintLogs" :key="m.id"><td>{{ m.date }}</td><td>{{ m.bus_name }}</td><td style="text-transform:capitalize;">{{ m.type }}</td><td>{{ m.description }}</td><td style="color:#EF4444;font-weight:600;">KSh {{ fmt(m.cost) }}</td><td>{{ m.vendor || '—' }}</td><td>{{ m.next_due_date || '—' }}</td></tr></tbody>
        </table>
        <div v-else style="text-align:center;padding:20px;color:#4A6A8A;">No maintenance records</div>
      </div>
    </div>

    <!-- LIVE MAP TAB -->
    <div v-if="tab==='map'">
      <div class="st-section" style="padding:14px;">
        <div style="font-size:13px;font-weight:600;color:#4FC3F7;margin-bottom:8px;">📍 Live Vehicle Locations</div>
        <div class="fl-map-grid">
          <div v-for="bus in busesWithGps" :key="bus.id" class="fl-map-card">
            <div class="d-flex justify-content-between">
              <div>
                <div style="font-weight:700;color:#E8EDF4;">{{ bus.name }}</div>
                <div style="font-size:11px;color:#4A6A8A;">{{ bus.registration_number }}</div>
              </div>
              <span class="fl-gps-badge">📡 {{ timeSince(bus.gps_updated_at) }}</span>
            </div>
            <div style="font-size:11px;color:#8BA4C4;margin-top:4px;">
              📍 {{ Number(bus.gps_lat).toFixed(5) }}, {{ Number(bus.gps_lng).toFixed(5) }}
              <span v-if="bus.driver_name"> · 👤 {{ bus.driver_name }}</span>
            </div>
            <a :href="'https://www.google.com/maps?q='+bus.gps_lat+','+bus.gps_lng" target="_blank" class="fl-map-link">🗺 Open in Google Maps</a>
          </div>
        </div>
        <div v-if="!busesWithGps.length" style="text-align:center;padding:30px;color:#4A6A8A;">
          <div style="font-size:48px;margin-bottom:8px;">📡</div>
          <p>No GPS data. Buses will appear here once GPS devices start reporting.</p>
        </div>
      </div>
    </div>

    <!-- VEHICLE FORM MODAL -->
    <div v-if="showVehicleModal" class="tt-overlay" @click.self="showVehicleModal=false"><div class="tt-modal" style="max-width:550px;">
      <h5 style="color:#E8EDF4;margin:0 0 16px;font-size:15px;">{{ vForm.id ? '✏️ Edit' : '➕ Add' }} Vehicle</h5>
      <div class="row g-3 mb-3">
        <div class="col-6"><label class="st-label">Name *</label><input class="st-input" v-model="vForm.name" placeholder="e.g. Bus 1" /></div>
        <div class="col-6"><label class="st-label">Registration *</label><input class="st-input" v-model="vForm.registrationNumber" placeholder="KBZ 123X" /></div>
      </div>
      <div class="row g-3 mb-3">
        <div class="col-4"><label class="st-label">Type</label><select class="st-input" v-model="vForm.vehicleType"><option value="bus">Bus</option><option value="minibus">Minibus</option><option value="van">Van</option><option value="car">Car</option><option value="motorcycle">Motorcycle</option></select></div>
        <div class="col-4"><label class="st-label">Capacity</label><input class="st-input" type="number" v-model.number="vForm.capacity" /></div>
        <div class="col-4"><label class="st-label">Color</label><input class="st-input" v-model="vForm.color" /></div>
      </div>
      <div class="row g-3 mb-3">
        <div class="col-4"><label class="st-label">Make</label><input class="st-input" v-model="vForm.make" placeholder="e.g. Toyota" /></div>
        <div class="col-4"><label class="st-label">Model</label><input class="st-input" v-model="vForm.model" placeholder="e.g. Coaster" /></div>
        <div class="col-4"><label class="st-label">Year</label><input class="st-input" type="number" v-model.number="vForm.year" /></div>
      </div>
      <div class="row g-3 mb-3">
        <div class="col-6"><label class="st-label">Route</label><input class="st-input" v-model="vForm.route" placeholder="e.g. Westlands - School" /></div>
        <div class="col-6"><label class="st-label">Status</label><select class="st-input" v-model="vForm.status"><option value="active">Active</option><option value="maintenance">In Maintenance</option><option value="grounded">Grounded</option><option value="retired">Retired</option></select></div>
      </div>
      <div class="row g-3 mb-3">
        <div class="col-6"><label class="st-label">Driver Name</label><input class="st-input" v-model="vForm.driverName" /></div>
        <div class="col-6"><label class="st-label">Driver Phone</label><input class="st-input" v-model="vForm.driverPhone" /></div>
      </div>
      <div class="row g-3 mb-3">
        <div class="col-6"><label class="st-label">Insurance Expiry</label><input class="st-input" type="date" v-model="vForm.insuranceExpiry" /></div>
        <div class="col-6"><label class="st-label">Inspection Expiry</label><input class="st-input" type="date" v-model="vForm.inspectionExpiry" /></div>
      </div>
      <div class="d-flex gap-2 justify-content-end">
        <button v-if="vForm.id" class="btn st-btn-danger btn-sm" @click="deleteVehicle" style="margin-right:auto;">🗑 Delete</button>
        <button class="btn st-btn-outline btn-sm" @click="showVehicleModal=false">Cancel</button>
        <button class="btn st-btn-primary btn-sm" @click="saveVehicle" :disabled="vSaving"><span v-if="vSaving" class="spinner-border spinner-border-sm me-1"></span>Save</button>
      </div>
      <div v-if="vMsg" class="st-alert mt-2" :class="vOk?'st-alert-success':'st-alert-danger'">{{ vMsg }}</div>
    </div></div>

    <!-- STUDENTS MODAL -->
    <div v-if="showStudentsModal" class="tt-overlay" @click.self="showStudentsModal=false"><div class="tt-modal" style="max-width:500px;">
      <h5 style="color:#E8EDF4;margin:0 0 16px;font-size:15px;">👥 {{ studentsBus?.name }} — Students</h5>
      <div class="d-flex gap-2 mb-3">
        <input class="st-input" v-model="studentSearch" placeholder="Search student to add..." @keyup.enter="searchStudent" style="flex:1;" />
        <button class="btn st-btn-primary btn-sm" @click="searchStudent">🔍</button>
      </div>
      <div v-if="searchResults.length" style="margin-bottom:12px;">
        <div v-for="s in searchResults" :key="s.id" class="d-flex justify-content-between align-items-center" style="padding:6px 8px;background:#0A1628;border-radius:6px;margin-bottom:4px;font-size:12px;">
          <span style="color:#E8EDF4;">{{ s.name }} ({{ s.admission_no }})</span>
          <button class="btn st-btn-primary btn-sm" style="padding:2px 10px;font-size:10px;" @click="assignStudent(s)">+ Add</button>
        </div>
      </div>
      <div style="font-size:12px;font-weight:600;color:#4FC3F7;margin-bottom:6px;">Assigned ({{ busStudents.length }})</div>
      <div v-for="s in busStudents" :key="s.id" class="d-flex justify-content-between align-items-center" style="padding:6px 8px;border-bottom:1px solid #1A3A6A;font-size:12px;">
        <div><strong style="color:#E8EDF4;">{{ s.student_name }}</strong> <span style="color:#4A6A8A;">{{ s.admission_no }} · {{ s.class_name }}</span></div>
        <button class="btn st-btn-danger btn-sm" style="padding:2px 8px;font-size:10px;" @click="removeStudent(s)">✕</button>
      </div>
      <div v-if="!busStudents.length" style="text-align:center;padding:15px;color:#4A6A8A;">No students assigned</div>
    </div></div>

    <!-- FUEL FORM MODAL -->
    <div v-if="showFuelModal" class="tt-overlay" @click.self="showFuelModal=false"><div class="tt-modal" style="max-width:420px;">
      <h5 style="color:#E8EDF4;margin:0 0 16px;font-size:15px;">⛽ Log Fuel</h5>
      <div class="row g-3 mb-3">
        <div class="col-6"><label class="st-label">Vehicle</label><select class="st-input" v-model="fForm.busId"><option v-for="b in buses" :key="b.id" :value="b.id">{{ b.name }}</option></select></div>
        <div class="col-6"><label class="st-label">Date</label><input class="st-input" type="date" v-model="fForm.date" /></div>
      </div>
      <div class="row g-3 mb-3">
        <div class="col-4"><label class="st-label">Litres</label><input class="st-input" type="number" step="0.1" v-model.number="fForm.litres" /></div>
        <div class="col-4"><label class="st-label">Cost (KSh)</label><input class="st-input" type="number" v-model.number="fForm.cost" /></div>
        <div class="col-4"><label class="st-label">Odometer</label><input class="st-input" type="number" v-model.number="fForm.odometer" /></div>
      </div>
      <div class="mb-3"><label class="st-label">Station</label><input class="st-input" v-model="fForm.station" placeholder="e.g. Shell Westlands" /></div>
      <div class="d-flex gap-2 justify-content-end"><button class="btn st-btn-outline btn-sm" @click="showFuelModal=false">Cancel</button><button class="btn st-btn-primary btn-sm" @click="saveFuel" :disabled="fSaving">Save</button></div>
    </div></div>

    <!-- MAINTENANCE FORM MODAL -->
    <div v-if="showMaintModal" class="tt-overlay" @click.self="showMaintModal=false"><div class="tt-modal" style="max-width:480px;">
      <h5 style="color:#E8EDF4;margin:0 0 16px;font-size:15px;">🔧 Log Maintenance</h5>
      <div class="row g-3 mb-3">
        <div class="col-6"><label class="st-label">Vehicle</label><select class="st-input" v-model="mForm.busId"><option v-for="b in buses" :key="b.id" :value="b.id">{{ b.name }}</option></select></div>
        <div class="col-6"><label class="st-label">Type</label><select class="st-input" v-model="mForm.type"><option value="service">Service</option><option value="repair">Repair</option><option value="tire">Tire</option><option value="body">Body Work</option><option value="electrical">Electrical</option><option value="inspection">Inspection</option><option value="other">Other</option></select></div>
      </div>
      <div class="row g-3 mb-3">
        <div class="col-6"><label class="st-label">Date</label><input class="st-input" type="date" v-model="mForm.date" /></div>
        <div class="col-6"><label class="st-label">Cost (KSh)</label><input class="st-input" type="number" v-model.number="mForm.cost" /></div>
      </div>
      <div class="mb-3"><label class="st-label">Description *</label><input class="st-input" v-model="mForm.description" placeholder="e.g. Full service, oil change, brake pads" /></div>
      <div class="row g-3 mb-3">
        <div class="col-6"><label class="st-label">Vendor</label><input class="st-input" v-model="mForm.vendor" /></div>
        <div class="col-6"><label class="st-label">Next Due Date</label><input class="st-input" type="date" v-model="mForm.nextDueDate" /></div>
      </div>
      <div class="d-flex gap-2 justify-content-end"><button class="btn st-btn-outline btn-sm" @click="showMaintModal=false">Cancel</button><button class="btn st-btn-primary btn-sm" @click="saveMaintenance" :disabled="mSaving">Save</button></div>
    </div></div>
  </div>
</div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/simi-auth/Navigation.vue";
import { executeJson, simitrack } from "@/api";
export default {
  name: 'FleetManagement',
  components: { Navigation },
  data() {
    const ld = () => { const d = new Date(); return d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0'); };
    return {
      tab:'vehicles', loading:false, buses:[], dashboard:null, alerts:[],
      // Vehicle form
      showVehicleModal:false, vSaving:false, vMsg:'', vOk:false,
      vForm:{id:'',name:'',registrationNumber:'',vehicleType:'bus',capacity:0,color:'',make:'',model:'',year:0,route:'',driverName:'',driverPhone:'',insuranceExpiry:'',inspectionExpiry:'',status:'active'},
      // Students
      showStudentsModal:false, studentsBus:null, busStudents:[], studentSearch:'', searchResults:[],
      // Fuel
      fuelLogs:[], fuelBusId:'', showFuelModal:false, fSaving:false,
      fForm:{busId:'',date:ld(),litres:0,cost:0,odometer:0,station:''},
      // Maintenance
      maintLogs:[], maintBusId:'', showMaintModal:false, mSaving:false,
      mForm:{busId:'',type:'service',date:ld(),cost:0,description:'',vendor:'',nextDueDate:''},
    };
  },
  computed: {
    busesWithGps() { return this.buses.filter(b => b.gps_lat && b.gps_lng); },
  },
  mounted() { this.loadFleet(); },
  methods: {
    fmt(v) { return Number(v||0).toLocaleString(); },
    typeIcon(t) { return {bus:'🚌',minibus:'🚐',van:'🚛',car:'🚗',motorcycle:'🏍'}[t]||'🚌'; },
    timeSince(dt) { if(!dt) return ''; const s=Math.floor((Date.now()-new Date(dt+'Z').getTime())/1000); if(s<60) return s+'s ago'; if(s<3600) return Math.floor(s/60)+'m ago'; if(s<86400) return Math.floor(s/3600)+'h ago'; return Math.floor(s/86400)+'d ago'; },

    async loadFleet() {
      this.loading=true;
      try {
        const r = await executeJson({function:'getFleetDashboard'},simitrack);
        if (r.data.success) {
          this.dashboard=r.data.data; this.buses=r.data.data?.buses||[]; this.alerts=r.data.data?.expiring_alerts||[];
        }
      } catch(e) {}
      this.loading=false;
    },
    openVehicleForm(bus) {
      if (bus) { this.vForm={id:bus.id,name:bus.name,registrationNumber:bus.registration_number,vehicleType:bus.vehicle_type||'bus',capacity:bus.capacity||0,color:bus.color||'',make:bus.make||'',model:bus.model||'',year:bus.year_of_manufacture||0,route:bus.route||'',driverName:bus.driver_name||'',driverPhone:bus.driver_phone||'',insuranceExpiry:bus.insurance_expiry||'',inspectionExpiry:bus.inspection_expiry||'',status:bus.status||'active'}; }
      else { this.vForm={id:'',name:'',registrationNumber:'',vehicleType:'bus',capacity:0,color:'',make:'',model:'',year:0,route:'',driverName:'',driverPhone:'',insuranceExpiry:'',inspectionExpiry:'',status:'active'}; }
      this.vMsg=''; this.showVehicleModal=true;
    },
    async saveVehicle() {
      if(!this.vForm.name) { this.vMsg='Name required'; this.vOk=false; return; }
      this.vSaving=true; this.vMsg='';
      try {
        const fn = this.vForm.id ? 'updateBus' : 'addBus';
        const p = {...this.vForm}; if(this.vForm.id) p.busId=this.vForm.id;
        const r = await executeJson({function:fn,...p},simitrack);
        this.vMsg=r.data.message; this.vOk=r.data.success;
        if(r.data.success) setTimeout(()=>{this.showVehicleModal=false;this.loadFleet();},800);
      } catch(e) { this.vMsg='Error'; }
      this.vSaving=false;
    },
    async deleteVehicle() {
      if(!confirm('Delete '+this.vForm.name+'?')) return;
      try { await executeJson({function:'deleteBus',busId:this.vForm.id},simitrack); this.showVehicleModal=false; this.loadFleet(); } catch(e) {}
    },

    // Students
    async openStudents(bus) { this.studentsBus=bus; this.busStudents=[]; this.searchResults=[]; this.studentSearch=''; this.showStudentsModal=true; await this.loadBusStudents(); },
    async loadBusStudents() {
      try { const r=await executeJson({function:'getBusStudents',busId:this.studentsBus.id},simitrack); if(r.data.success) this.busStudents=r.data.data||[]; } catch(e){}
    },
    async searchStudent() {
      if(!this.studentSearch) return;
      try { const r=await executeJson({function:'searchStudents',query:this.studentSearch},simitrack); if(r.data.success) this.searchResults=r.data.data||[]; } catch(e){}
    },
    async assignStudent(s) {
      try { await executeJson({function:'assignStudentBus',studentId:s.id,busId:this.studentsBus.id},simitrack); this.searchResults=[]; this.studentSearch=''; this.loadBusStudents(); this.loadFleet(); } catch(e){}
    },
    async removeStudent(s) {
      if(!confirm('Remove '+s.student_name+'?')) return;
      try { await executeJson({function:'removeStudentBus',id:s.id},simitrack); this.loadBusStudents(); this.loadFleet(); } catch(e){}
    },

    // Fuel
    async loadFuel() { try { const r=await executeJson({function:'getFuelLogs',busId:this.fuelBusId},simitrack); if(r.data.success) this.fuelLogs=r.data.data||[]; } catch(e){} },
    openFuelForm(bus) { this.fForm.busId=bus?.id||this.buses[0]?.id||''; this.showFuelModal=true; },
    async saveFuel() {
      this.fSaving=true;
      try { const r=await executeJson({function:'addFuelLog',...this.fForm},simitrack); if(r.data.success){this.showFuelModal=false;this.loadFuel();this.loadFleet();} } catch(e){}
      this.fSaving=false;
    },

    // Maintenance
    async loadMaintenance() { try { const r=await executeJson({function:'getMaintenanceLogs',busId:this.maintBusId},simitrack); if(r.data.success) this.maintLogs=r.data.data||[]; } catch(e){} },
    openMaintenanceForm(bus) { this.mForm.busId=bus?.id||this.buses[0]?.id||''; this.showMaintModal=true; },
    async saveMaintenance() {
      if(!this.mForm.description) return;
      this.mSaving=true;
      try { const r=await executeJson({function:'addMaintenance',...this.mForm},simitrack); if(r.data.success){this.showMaintModal=false;this.loadMaintenance();this.loadFleet();} } catch(e){}
      this.mSaving=false;
    },
  }
};
</script>

<style scoped>
.st-page{min-height:100vh;background:#0A1628;padding-top:70px;}.st-back{color:#8BA4C4;font-size:22px;text-decoration:none;}.st-back:hover{color:#4FC3F7;}.st-page-title{color:#E8EDF4;font-size:18px;font-weight:700;margin:0;}
.st-section{background:#0F2347;border:1px solid #1A3A6A;border-radius:14px;padding:20px;margin-bottom:16px;}.st-label{color:#8BA4C4;font-size:12px;font-weight:600;margin-bottom:4px;display:block;}
.st-input{width:100%;background:#0A1628;border:1px solid #1A3A6A;border-radius:8px;padding:8px 12px;color:#E8EDF4;font-size:13px;outline:none;}.st-input:focus{border-color:#2E86DE;}
.st-btn-primary{background:linear-gradient(135deg,#2E86DE,#1A6BC4);color:#fff;border:none;font-weight:600;border-radius:8px;padding:8px 16px;font-size:13px;}.st-btn-outline{background:transparent;border:1px solid #1A3A6A;color:#8BA4C4;font-weight:600;border-radius:8px;padding:8px 16px;font-size:13px;}.st-btn-outline:hover{border-color:#4FC3F7;color:#4FC3F7;}
.st-btn-danger{background:rgba(239,68,68,.15);border:1px solid #EF4444;color:#EF4444;border-radius:8px;}.st-alert{padding:10px 14px;border-radius:10px;font-size:13px;}.st-alert-danger{background:rgba(239,68,68,.1);border:1px solid rgba(239,68,68,.2);color:#EF4444;}
.st-spinner{display:flex;justify-content:center;padding:30px;}.st-spinner .spinner-border{color:#2E86DE;}
.fl-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:10px;margin-bottom:16px;}.fl-card{background:#0F2347;border:1px solid #1A3A6A;border-radius:10px;padding:14px;}.fl-label{font-size:10px;color:#4A6A8A;text-transform:uppercase;font-weight:600;}.fl-value{font-size:20px;font-weight:800;color:#E8EDF4;}.fl-sub{font-size:11px;color:#8BA4C4;}
.fl-bus-card{background:#0F2347;border:1px solid #1A3A6A;border-radius:12px;padding:16px;margin-bottom:10px;border-left:4px solid #10B981;transition:.2s;}
.fl-status-maintenance{border-left-color:#F59E0B;}.fl-status-grounded{border-left-color:#EF4444;}.fl-status-retired{border-left-color:#4A6A8A;opacity:.6;}
.fl-type-badge{font-size:28px;}.fl-bus-name{font-size:16px;font-weight:700;color:#E8EDF4;}.fl-bus-reg{font-size:12px;color:#4FC3F7;font-weight:600;}
.fl-bus-details{display:flex;flex-wrap:wrap;gap:12px;font-size:11px;color:#8BA4C4;}.fl-bus-alerts{display:flex;flex-wrap:wrap;gap:6px;}
.fl-alert-badge{font-size:10px;background:rgba(239,68,68,.1);color:#EF4444;padding:2px 8px;border-radius:4px;border:1px solid rgba(239,68,68,.2);}
.fl-gps-badge{font-size:10px;background:rgba(16,185,129,.1);color:#10B981;padding:2px 8px;border-radius:4px;}
.fl-status-badge{font-size:10px;font-weight:600;padding:3px 8px;border-radius:4px;text-align:center;text-transform:uppercase;}
.st-active{background:rgba(16,185,129,.1);color:#10B981;}.st-maintenance{background:rgba(245,158,11,.1);color:#F59E0B;}.st-grounded{background:rgba(239,68,68,.1);color:#EF4444;}.st-retired{background:rgba(74,106,138,.1);color:#4A6A8A;}
.fl-map-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:10px;}.fl-map-card{background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:12px;}
.fl-map-link{display:inline-block;margin-top:6px;font-size:11px;color:#4FC3F7;text-decoration:none;}.fl-map-link:hover{text-decoration:underline;}
.la-tabs{display:flex;gap:4px;margin-bottom:16px;border-bottom:2px solid #1A3A6A;}.la-tab{background:none;border:none;padding:10px 16px;color:#4A6A8A;font-size:13px;font-weight:600;cursor:pointer;border-bottom:2px solid transparent;margin-bottom:-2px;}.la-tab.active{color:#4FC3F7;border-bottom-color:#4FC3F7;}
.la-table{width:100%;border-collapse:collapse;font-size:12px;color:#E8EDF4;}.la-table th{background:#0A1628;padding:6px 8px;color:#4FC3F7;font-size:10px;text-transform:uppercase;border-bottom:2px solid #1A3A6A;}.la-table td{padding:5px 8px;border-bottom:1px solid #1A3A6A;}
.tt-overlay{position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,.7);display:flex;align-items:center;justify-content:center;z-index:999;}.tt-modal{background:#0D2B5E;border-radius:14px;padding:24px;width:95%;max-width:520px;max-height:90vh;overflow-y:auto;border:1px solid #1A3A6A;}
</style>
